import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import Confirm from 'assets/components/page/templates/Confirm';
import Modal from 'assets/components/page/templates/Modal';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import ObjectTable from 'assets/templates/objectTable/ObjectTable';
import { HttpStatus } from 'config/Api.Config';
import { keys, omit, values } from 'lodash';
import Claim from 'models/claims/claim/Model.Claim';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import claimJobProcessUsBankPaymentApi from 'models/claims/claimJob/processUsBankPayment/Api.ClaimJob.ProcessUsBankPayment';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import PaymentType from 'models/enums/PaymentType';
import usBankCancelApi from 'models/integration/usBank/cancel/Api.UsBank.Cancel';
import usBankInfoApi from 'models/integration/usBank/info/Api.UsBank.Info';
import usBankUpdateApi from 'models/integration/usBank/update/Api.UsBank.Update';
import { useEffect, useState } from 'react';

type PaymentData = {
  claimJobId: number;
  claimInvoiceId: number;
  payeeId: number;
  clientId: number;
  dealerId: number;
  email: string;
};
type PaymentItem = {
  key: string;
  jobItemId: number;
  jobId: number;
  payee: ClaimJobItemPayee;
  contactEmail: string;
  jobItemTypeCode: string;
  currency: string;
  paymentMethodCode: string;
  parts: number;
  labour: number;
  other: number;
  deduct: number;
  readonly ttl: number;
  gstHst: number;
  provincialSalesTax: number;
  readonly totalTaxAmount: number;
  readonly total: number;
  pendingPaymentAmount: number;
  paidAmount: number;
  usBankVirtualCardId: string;
};
type UsBankAction = 'create' | 'modify' | 'cancel' | 'info';
type ModifyPayment = {
  amount: number;
  usBankVirtualCardId: string;
};

const UsBankPayment = ({
  claim,
  claimJob,
  paymentItem,
  isActive,
  reload,
}: {
  claim: Claim;
  claimJob: ClaimJob;
  paymentItem: PaymentItem;
  isActive: boolean;
  reload(): void;
}) => {
  const { lang } = getLocales();
  const { permissions, userId } = useAuthStore();

  const [pendingPayment, setPendingPayment] = useState<PaymentData & { payeeName?: string }>();
  const [modfiyPayment, setModifyPayment] = useState<ModifyPayment>(undefined);
  const [usBankAction, setUsBankAction] = useState<UsBankAction>(undefined);
  const [paymentInfo, setPaymentInfo] = useState<Model.IVirtualCardStatus>(undefined);

  const currentJobStatus = claimJob?.statusCode || ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING;
  const usBankCardId = paymentItem?.usBankVirtualCardId;

  const isClaimOwner = userId === claim.ownerId;

  useEffect(() => {
    if (usBankCardId) {
      (async () => {
        const res = await usBankInfoApi({ usBankCardId });
        if (res.status === HttpStatus.ok) setPaymentInfo(res.payload);
      })();
    }
  }, [usBankCardId]);

  return (
    <>
      {pendingPayment &&
        (permissions?.CLAIM_ADMIN || (isClaimOwner && permissions?.MODIFY_CLAIMJOB_USBANK_PAYMENT)) &&
        usBankAction === 'create' && (
          <Modal
            header={lang.usBankCreatePayment}
            subcomponents={ThemeModalDefault}
            onClose={() => {
              setPendingPayment(undefined);
              setUsBankAction(undefined);
            }}
            render={
              <FormWrapper>
                <div style={{ textAlign: 'center', fontWeight: 600 }}>{lang.sendPaymentRequestTo}:</div>
                <p style={{ textAlign: 'center' }}>{pendingPayment?.payeeName}</p>
                <SelectInput
                  name="claimInvoiceId"
                  label={lang.invoices}
                  data={claim?.claimInvoices?.map((it) => it.displayInfo)}
                  value={pendingPayment?.claimInvoiceId}
                  error={!pendingPayment?.claimInvoiceId && lang.required}
                  onChange={({ claimInvoiceId }) => setPendingPayment((prev) => ({ ...prev, claimInvoiceId }))}
                />
                <TextInput
                  name="email"
                  label={lang.email}
                  value={pendingPayment?.email}
                  error={!pendingPayment?.email && lang.required}
                  onChange={({ email }) => setPendingPayment((prev) => ({ ...prev, email }))}
                />
              </FormWrapper>
            }
            footer={
              <Button
                class={ThemeButtonCircle}
                render={lang.sendPaymentRequest}
                disabled={!pendingPayment?.claimInvoiceId}
                onClick={async () => {
                  await claimJobProcessUsBankPaymentApi(omit(pendingPayment, 'payeeName'));
                  if (reload) reload();
                }}
                active
              />
            }
          />
        )}
      {modfiyPayment &&
        (permissions?.CLAIM_ADMIN || (isClaimOwner && permissions?.MODIFY_CLAIMJOB_USBANK_PAYMENT)) &&
        usBankAction === 'modify' && (
          <Modal
            header={lang.usBankModifyPayment}
            subcomponents={ThemeModalDefault}
            onClose={() => setUsBankAction(undefined)}
            render={
              <FormWrapper>
                <NumberInput
                  name="amount"
                  label={lang.amount}
                  value={modfiyPayment?.amount}
                  error={!modfiyPayment?.amount && lang.required}
                  onChange={({ amount }) => setModifyPayment((prev) => ({ ...prev, amount }))}
                />
              </FormWrapper>
            }
            footer={
              <Button
                class={ThemeButtonCircle}
                render={lang.modifyPayment}
                disabled={!modfiyPayment?.amount}
                onClick={async () => {
                  await usBankUpdateApi(
                    { usBankCardId: modfiyPayment?.usBankVirtualCardId },
                    { amount: modfiyPayment?.amount }
                  );
                  if (reload) reload();
                }}
                active
              />
            }
          />
        )}
      {usBankCardId &&
        (permissions?.CLAIM_ADMIN || (isClaimOwner && permissions?.CANCEL_CLAIMJOB_USBANK_PAYMENT)) &&
        usBankAction === 'cancel' && (
          <Confirm
            subcomponents={ThemeConfirmDefault}
            actionName={`${lang.cancelPayment} for payee: ${paymentItem.payee?.displayInfo?.title}`}
            confirmationType={'nextButton'}
            onConfirm={async () => {
              await usBankCancelApi({ usBankCardId: usBankCardId });
              if (reload) reload();
            }}
          />
        )}
      {paymentInfo && usBankAction === 'info' && (
        <Modal
          header={lang.usBankInfoPayment}
          subcomponents={ThemeModalDefault}
          onClose={() => setUsBankAction(undefined)}
          render={<ObjectTable paths={keys(paymentInfo)} data={values(paymentInfo)} />}
        />
      )}
      <DropdownButton
        title={lang.usBankOptions}
        buttonMedia={<Icon class="fas-money-check-alt" />}
        horizontalPosition="auto_fixed"
        verticalPosition="auto_fixed"
        subcomponents={ThemeDropdownButtonPopup}
        htmlElementProps={{ title: lang.usBankOptions }}
        dropdownOptions={
          <>
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-money-check-alt" />}
              render={lang.createPayment}
              htmlElementProps={{
                button: {
                  title:
                    paymentItem.paymentMethodCode !== PaymentType.usBank || paymentItem.currency !== 'CAD'
                      ? lang.paymentIsAvailableOnlyFor
                      : currentJobStatus !== ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING
                      ? lang.paymentRequiresClaimToBeInCorrectStatus
                      : !paymentItem.jobId || isActive
                      ? lang.pleaseSaveJobToDoPayment
                      : paymentItem.pendingPaymentAmount
                      ? lang.youAlreadyHavePendingPayment
                      : paymentItem.total - paymentItem.paidAmount <= 0
                      ? lang.alreadyPaid
                      : lang.sendPaymentRequest,
                },
              }}
              disabled={
                !(permissions?.CLAIM_ADMIN || (isClaimOwner && permissions?.MODIFY_CLAIMJOB_USBANK_PAYMENT)) ||
                !(
                  currentJobStatus == ClaimJobStatusCode.OPEN_APPROVED_TO_PAY_BY_ACCOUNTING &&
                  paymentItem.currency === 'CAD' &&
                  paymentItem.jobId &&
                  paymentItem.total - paymentItem.paidAmount > 0 &&
                  !paymentItem.pendingPaymentAmount &&
                  paymentItem.paymentMethodCode === PaymentType.usBank &&
                  !isActive
                )
              }
              onClick={(data) => {
                setUsBankAction('create');
                setPendingPayment(data);
              }}
              data={{
                email: paymentItem.payee?.dealer?.claimPaymentEmail ?? claim?.createdByPayee?.email,
                claimJobId: paymentItem.jobId,
                claimInvoiceId: undefined,
                payeeId: paymentItem.payee?.payeeId,
                dealerId: paymentItem.payee?.dealerId,
                clientId: paymentItem.payee?.clientId,
                payeeName: paymentItem.payee?.displayInfo?.title
                  ? `${paymentItem.payee?.displayInfo?.title} (${paymentItem.total - paymentItem.paidAmount} ${
                      paymentItem.currency
                    })`
                  : undefined,
              }}
            />
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-money-check-alt" />}
              render={lang.modifyPayment}
              onClick={setUsBankAction}
              data="modify"
              htmlElementProps={{ title: lang.usBankModifyPayment }}
              disabled={
                !(permissions?.CLAIM_ADMIN || (isClaimOwner && permissions?.MODIFY_CLAIMJOB_USBANK_PAYMENT)) ||
                !usBankCardId
              }
            />
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-money-check-alt" />}
              render={lang.infoPayment}
              onClick={setUsBankAction}
              data="info"
              disabled={!usBankCardId}
              htmlElementProps={{ title: lang.usBankInfoPayment }}
            />
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-money-check-alt" />}
              render={lang.cancelPayment}
              onClick={setUsBankAction}
              data="cancel"
              disabled={
                !(permissions?.CLAIM_ADMIN || (isClaimOwner && permissions?.CANCEL_CLAIMJOB_USBANK_PAYMENT)) ||
                !usBankCardId
              }
              htmlElementProps={{ title: lang.usBankCancelPayment }}
            />
          </>
        }
      />
    </>
  );
};

export default UsBankPayment;
